import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import api, { endpoints } from '../../services/api';
import ReporteDestinosPDF from '../../components/PDF/ReporteDestinosPDF';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  Public as DestinoIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

const ReporteDestinos = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    fecha_inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    fecha_fin: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(filters).toString();
      const response = await api.get(`${endpoints.reportes.destinos}?${params}`);
      setData(response.data.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DestinoIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Carga por Destino
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>
                Análisis de distribución de carga por destino
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fecha Inicio"
                type="date"
                name="fecha_inicio"
                value={filters.fecha_inicio}
                onChange={handleFilterChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fecha Fin"
                type="date"
                name="fecha_fin"
                value={filters.fecha_fin}
                onChange={handleFilterChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                onClick={fetchData}
                startIcon={<SearchIcon />}
                fullWidth
                sx={{ height: 56 }}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Resumen */}
      {data?.resumen && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.50' }}>
              <Typography variant="h3" fontWeight="bold" color="primary.main">
                {data.resumen.total_destinos}
              </Typography>
              <Typography color="text.secondary">Destinos Atendidos</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'success.50' }}>
              <Typography variant="h3" fontWeight="bold" color="success.main">
                {(data.resumen.peso_total_kg / 1000).toFixed(1)}t
              </Typography>
              <Typography color="text.secondary">Peso Total</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'info.50' }}>
              <Typography variant="h3" fontWeight="bold" color="info.main">
                {Math.round(data.resumen.peso_total_lbs).toLocaleString()}
              </Typography>
              <Typography color="text.secondary">Libras</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Table */}
      <Card sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Destino</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell align="center">Vuelos</TableCell>
                <TableCell align="center">Pallets</TableCell>
                <TableCell align="right">Peso (kg)</TableCell>
                <TableCell align="right">Peso (lbs)</TableCell>
                <TableCell sx={{ width: 200 }}>Distribución</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data?.destinos?.length > 0 ? (
                data.destinos.map((d) => (
                  <TableRow key={d.destino} hover>
                    <TableCell>
                      <Chip label={d.destino} color="primary" />
                    </TableCell>
                    <TableCell>{d.nombre_destino}</TableCell>
                    <TableCell align="center">{d.total_vuelos}</TableCell>
                    <TableCell align="center">{d.total_pallets}</TableCell>
                    <TableCell align="right">{d.peso_total_kg.toLocaleString()}</TableCell>
                    <TableCell align="right">{Math.round(d.peso_total_lbs).toLocaleString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={d.porcentaje}
                          sx={{ flex: 1, height: 10, borderRadius: 5 }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 45 }}>
                          {d.porcentaje}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No hay datos en el rango seleccionado</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {data && (
          <PDFDownloadLink
            document={<ReporteDestinosPDF data={data} filters={filters} />}
            fileName={`reporte-destinos-${filters.fecha_inicio}-${filters.fecha_fin}.pdf`}
            style={{ textDecoration: 'none' }}
          >
            {({ loading: pdfLoading }) => (
              <Button
                variant="contained"
                color="success"
                startIcon={pdfLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                disabled={pdfLoading}
              >
                {pdfLoading ? 'Generando...' : 'Descargar PDF'}
              </Button>
            )}
          </PDFDownloadLink>
        )}
        <Button component={Link} to="/reportes" variant="contained" color="inherit" startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>
    </Box>
  );
};

export default ReporteDestinos;

